import React, { useState } from 'react';
import { useCartStore } from '@/ui/store/cart';
import { CartBundleItem } from '@/ui/store/cart/types';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { ProductBadge } from '@/ui/components/product-badge/product-badge';
import { CLUB_EXCLUSIVE_BADGE_KEY } from '@/models/product-badge-model';
import { CartService } from '@/ui/services/CartService';
import { twJoin } from 'tailwind-merge';
import CartProductImage from '../CartProductImage';
import CartProductInfo from '../CartProductInfo';
import RemovalConfirmation from '../RemovalConfirmation';
import { BundleProductCartItem } from './bundle-product-cart-item/bundle-product-cart-item';
import QuantityDisplay from '../ProductQuantityDisplay';
import { CartItemVariant } from '../types/CartItemVariant';

export interface CartBundleComponentProps {
  bundle: CartBundleItem;
  disableUpdate?: boolean;
  variant?: CartItemVariant;
  currency?: string;
}

export function CartBundleComponent({
  bundle,
  disableUpdate,
  variant,
  currency,
}: Readonly<CartBundleComponentProps>) {
  const { removeBundle, updateBundleQuantity, toggleCartDisplay } = useCartStore();
  const clubBadge = useProductRuleService()
    .getBundleBadges({
      bundleId: bundle.id,
      selectedVariants: bundle.productVariants.map(({ id, sku }) => ({ productId: id, sku })),
    })
    .find(({ key }) => key === CLUB_EXCLUSIVE_BADGE_KEY);
  const [isMarkedForRemoval, setIsMarkedForRemoval] = useState(false);

  const isLargeImage = variant?.imageSize === 'lg';

  const updateQuantity = (quantity: number) => {
    if (quantity > 0) {
      updateBundleQuantity(bundle.cartBundleId, quantity);
      setIsMarkedForRemoval(false); // Cancel removal if quantity is increased
    } else {
      setIsMarkedForRemoval(true); // Mark for removal if quantity is zero or less
    }
  };

  const confirmRemoval = () => {
    removeBundle(bundle.cartBundleId, bundle.upgradeProgramDeviceId);
  };

  return (
    <div
      className={twJoin(
        !isLargeImage && 'px-4 sm:px-6',
        bundle ? 'py-2' : 'py-3',
        isMarkedForRemoval && 'bg-neutral-5',
      )}
    >
      <div className="flex">
        <CartProductImage
          image={bundle.image}
          productName={bundle.name}
          toggleCartDisplay={disableUpdate ? undefined : toggleCartDisplay}
          quantity={bundle.quantity}
          variant={variant}
        />
        <div className="ml-4 flex flex-1 flex-col justify-between">
          <div>
            <CartProductInfo
              name={bundle.name}
              discount={CartService.calculateBundleDiscount(bundle)}
              price={CartService.calculateBundlePrice(bundle)}
              toggleCartDisplay={disableUpdate ? undefined : toggleCartDisplay}
              variant={variant}
              nukiId={bundle.upgradeProgramDeviceId}
              currency={currency}
            />
            {clubBadge && (
              <ProductBadge
                label={clubBadge.label}
                color={clubBadge.color}
                icon={clubBadge.icon}
                className="mb-3"
              />
            )}
          </div>

          {isMarkedForRemoval ? (
            <RemovalConfirmation
              cancelRemoval={() => setIsMarkedForRemoval(false)}
              confirmRemoval={confirmRemoval}
            />
          ) : (
            <QuantityDisplay
              quantity={bundle.quantity}
              fixedQuantity={bundle.upgradeProgramDeviceId ? true : undefined}
              disableUpdate={disableUpdate}
              updateQuantity={updateQuantity}
            />
          )}
        </div>
      </div>
      <div className="mt-2">
        {bundle.productVariants.map((bundleProduct, index) => (
          <BundleProductCartItem
            key={`${bundleProduct.cartItemId}-${index}`}
            product={bundleProduct}
            index={index}
            variant={variant}
            disableUpdate={disableUpdate}
          />
        ))}
      </div>
    </div>
  );
}
